import { Entity, Firm, FirmType, MPID, STPMsgType, STPRoute } from 'api/interfaces/firm';
import {
  CustomFieldProps,
  FieldDefinition,
  FieldType,
} from 'components/Form/components/Field/types';
import { isBusinessUnitsEnabled } from 'config/features';
import { SMSession } from 'interfaces/session';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedBusinessUnit } from 'redux/actions/firms/setSelectedBusinessUnit';
import { ApplicationState } from 'redux/applicationState';
import { BrokerageWidget } from 'routes/EntityManagement/FirmsManagement/components/BrokerageWidget';
import { EntityGroup } from 'routes/EntityManagement/FirmsManagement/components/EntityGroup';
import { EntityWidget } from 'routes/EntityManagement/FirmsManagement/components/EntityWidget';
import { ApplicationsDropdown } from 'routes/EntityManagement/FirmsManagement/components/FirmsForm/components/ApplicationsDropdown';
import { DateInput } from 'routes/EntityManagement/FirmsManagement/components/FirmsForm/components/DateInput';
import { MPIDItem } from 'routes/EntityManagement/FirmsManagement/components/FirmsForm/components/MPIDItem';
import { Brokerage } from 'types/brokerage';
import { DropdownOption } from 'types/dropdownOption';
import { WhitelistItem } from 'types/whitelistItem';

const isNDForFXO = (firm: Firm): boolean => {
  const { firmCategory } = firm;
  return firmCategory?.some(
    (item: DropdownOption): boolean => item.id === 'fxo' || item.id === 'ndf'
  );
};

const firmCategories = [
  {
    id: 'fxo',
    name: 'FXO',
  },
  {
    id: 'ndf',
    name: 'NDF',
  },
];

export const useFields = (): ReadonlyArray<FieldDefinition<Firm, readonly SMSession[]>> => {
  const { selectedBusinessUnit, firmsById, isConnectivity, firmsTypes } = useSelector(
    (state: ApplicationState) => state.firms
  );
  const dispatch = useDispatch();

  const getSelectedBUValue = (): string | null => {
    if (!selectedBusinessUnit) return null;
    return typeof selectedBusinessUnit === 'string'
      ? selectedBusinessUnit
      : selectedBusinessUnit.BU;
  };

  const selectedBUValue = getSelectedBUValue();

  return [
    {
      name: 'basicInformation',
      fieldType: FieldType.group,
      display: true,
      children: [
        {
          name: 'basicInformation',
          fieldType: FieldType.fieldset,
          label: 'Basic Information',
          display: true,
          children: [
            {
              name: 'firmLabel',
              fieldType: FieldType.text,
              label: 'Firm Name',
              display: true,
              required: true,
            },
            {
              name: 'firmType',
              fieldType: FieldType.dropdown,
              label: 'Type',
              options: firmsTypes.map(
                (value: string): DropdownOption => ({ name: value, id: value })
              ),
              display: true,
              required: true,
            },
            {
              name: 'firmCategory',
              label: 'Firm Type',
              fieldType: FieldType.multiBool,
              required: (firm: Firm): boolean => {
                switch (firm.firmType) {
                  case FirmType.Bank:
                  case FirmType.Broker:
                  case FirmType.STP:
                  case FirmType.SEF:
                    return true;
                  case FirmType.Connectivity:
                  default:
                    return false;
                }
              },
              display: (firm: Firm): boolean => {
                switch (firm.firmType) {
                  case FirmType.Bank:
                  case FirmType.Broker:
                  case FirmType.STP:
                  case FirmType.SEF:
                    return true;
                  case FirmType.Connectivity:
                  default:
                    return false;
                }
              },
              options: firmCategories,
            },
          ],
        },
        {
          name: 'effectiveDateInformation',
          fieldType: FieldType.fieldset,
          // Blank legend keeps Effective Date vertically aligned with Firm Name
          label: '\u00a0',
          display: true,
          children: [
            {
              name: 'effectiveDate',
              fieldType: FieldType.custom,
              label: 'Effective Date',
              display: true,
              required: false,
              component: DateInput,
            },
          ],
        },
      ],
    },
    {
      name: 'addressesAndPhoneNumbers',
      fieldType: FieldType.group,
      display: true,
      children: [
        {
          name: 'addresses',
          fieldType: FieldType.fieldset,
          label: 'Business Address',
          display: true,
          children: [
            {
              name: 'businessAddress',
              fieldType: FieldType.text,
              label: 'Address Line 1',
              display: true,
              required: false,
            },
            {
              name: 'address2',
              fieldType: FieldType.text,
              label: 'Address Line 2',
              display: true,
              required: false,
            },
            {
              name: 'address3',
              fieldType: FieldType.text,
              label: 'Address Line 3',
              display: true,
              required: false,
            },
            {
              name: 'city',
              fieldType: FieldType.text,
              label: 'City',
              display: true,
              required: false,
            },
            {
              name: 'state',
              fieldType: FieldType.text,
              label: 'State',
              display: true,
              required: false,
            },
            {
              name: 'postalCode',
              fieldType: FieldType.text,
              label: 'Postal Code',
              display: true,
              required: false,
            },
            {
              name: 'country',
              fieldType: FieldType.text,
              label: 'Country',
              display: true,
              required: false,
            },
          ],
        },
        {
          name: 'phoneNumbers',
          fieldType: FieldType.fieldset,
          label: 'Phone Numbers',
          display: true,
          children: [
            {
              name: 'workPhone',
              fieldType: FieldType.phone,
              label: 'Work Phone',
              display: true,
              required: false,
            },
            {
              name: 'cellPhone',
              fieldType: FieldType.phone,
              label: 'Cell Phone',
              display: true,
              required: false,
            },
          ],
        },
      ],
    },
    {
      fieldType: FieldType.fieldset,
      label: 'Route Information',
      name: 'route',
      display: (firm: Firm): boolean => {
        return (
          firm.firmType !== FirmType.Broker && (isNDForFXO(firm) || firm.firmType === FirmType.STP)
        );
      },
      children: [
        {
          name: 'STPRoute',
          fieldType: FieldType.dropdown,
          label: 'STP Route',
          options: Object.values(STPRoute).map(
            (value: string): DropdownOption => ({ name: value, id: value })
          ),
          display: true,
          required: (firm: Firm): boolean =>
            firm.firmType !== FirmType.Broker &&
            (isNDForFXO(firm) || firm.firmType === FirmType.STP),
        },
        {
          name: 'STPMsgType',
          fieldType: FieldType.dropdown,
          label: 'STP Message Type',
          options: Object.values(STPMsgType).map(
            (value: string): DropdownOption => ({ name: value, id: value })
          ),
          display: (firm: Firm): boolean =>
            firm.firmType !== FirmType.Broker && firm.STPRoute === STPRoute.DIRECT,
          required: (firm: Firm): boolean =>
            firm.firmType !== FirmType.Broker && firm.STPRoute === STPRoute.DIRECT,
        },
        {
          name: 'STPDestination',
          fieldType: FieldType.text,
          label: 'STP Destination',
          display: (firm: Firm): boolean =>
            firm.firmType === FirmType.SEF ||
            firm.firmType === FirmType.STP ||
            firm.STPRoute === STPRoute.DIRECT,
          required: (firm: Firm): boolean =>
            firm.firmType === FirmType.SEF ||
            firm.firmType === FirmType.STP ||
            firm.STPRoute === STPRoute.DIRECT,
        },
      ],
    },
    {
      name: 'BU',
      fieldType: FieldType.array,
      label: 'Business Units',
      display: (firm: Firm): boolean =>
        isBusinessUnitsEnabled &&
        (firm?.BU?.length > 0 ||
          firmsById[0]?.firmType === FirmType.Connectivity ||
          firm.firmType === FirmType.Connectivity),
      required: false,
      fields: [
        {
          name: 'BU',
          fieldType: FieldType.text,
          label: 'Business Unit Id',
          required: true,
          display: true,
        },
      ],
      render: (value: string | Record<string, string>): React.ReactElement => {
        return (
          <span
            style={{
              background: selectedBusinessUnit === value ? '#0989f1' : '',
              color: selectedBusinessUnit === value ? '#fff' : '',
            }}
          >
            {typeof value === 'string' ? value : value.BU}
          </span>
        );
      },
      onClick: (value: any): void => {
        const clickedBU = typeof value === 'string' ? value : value.BU;
        const currentBU =
          typeof selectedBusinessUnit === 'string'
            ? selectedBusinessUnit
            : selectedBusinessUnit?.BU;

        if (clickedBU === currentBU) {
          dispatch(setSelectedBusinessUnit(null));
        } else {
          dispatch(setSelectedBusinessUnit(value));
        }
      },
    },
    {
      name: 'MPIDS',
      fieldType: FieldType.array,
      label: 'MPIDs',
      display: true,
      required: true,
      filter: (mpids: readonly MPID[]): readonly MPID[] => {
        if (!isBusinessUnitsEnabled || !selectedBUValue || !firmsById[0]?.BU?.length) {
          return mpids;
        }
        return mpids.filter((mpid: MPID) => {
          const mpidBU = typeof mpid === 'object' && 'BU' in mpid ? (mpid as any).BU : mpid.appid;
          return mpidBU === selectedBUValue;
        });
      },
      fields: [
        {
          name: 'mpid',
          fieldType: FieldType.text,
          label: 'MPID',
          required: true,
          display: true,
        },
        // {
        //   name:
        //     firmsById[0]?.BU?.length > 0 || firmsById[0]?.firmType === FirmType.Connectivity
        //       ? 'BU'
        //       : 'appid',
        //   fieldType: FieldType.text,
        //   label:
        //     firmsById[0]?.BU?.length > 0 || firmsById[0]?.firmType === FirmType.Connectivity
        //       ? 'Business Unit'
        //       : 'Application',
        //   required: true,
        //   display: true,
        // },
        ...(isBusinessUnitsEnabled &&
        (firmsById[0]?.BU?.length > 0 ||
          firmsById[0]?.firmType === FirmType.Connectivity ||
          isConnectivity)
          ? [
              {
                name: 'BU',
                fieldType: FieldType.text,
                label: 'Business Unit',
                required: true,
                display: true,
              } as FieldDefinition<any, readonly SMSession[]>,
            ]
          : [
              {
                name: 'appid',
                label: 'Application',
                fieldType: FieldType.custom,
                required: true,
                display: true,
                component: ApplicationsDropdown,
              } as FieldDefinition<any, readonly SMSession[]>,
            ]),
      ],
      render: (value: MPID): React.ReactElement => {
        return <MPIDItem mpid={value} />;
      },
    },
    {
      name: 'sessionIds',
      label: 'Session IDs',
      fieldType: FieldType.multiBool,
      display: (firm: Firm): boolean => firm.firmType === FirmType.Connectivity,
      required: (firm: Firm): boolean => firm.firmType === FirmType.Connectivity,
      options: (_: any, sessions: readonly SMSession[]): readonly DropdownOption[] => {
        return sessions.map(
          (session: SMSession): DropdownOption => ({
            id: session.id,
            name: session.id,
          })
        );
      },
    },
    {
      name: 'whitelist',
      fieldType: FieldType.array,
      label: 'Whitelisted IPs',
      display: (firm: Firm): boolean => isNDForFXO(firm),
      render: (value: WhitelistItem): React.ReactNode => {
        return <div>{value.ipAddress}</div>;
      },
      required: false,
      fields: [
        {
          name: 'ipAddress',
          fieldType: FieldType.ipAddress,
          label: 'IP Address',
          display: true,
          required: true,
        },
      ],
    },
    {
      name: 'entities',
      fieldType: FieldType.array,
      label: 'Entities',
      display: (firm: Firm): boolean =>
        isNDForFXO(firm) && firm.firmType !== FirmType.STP && firm.firmType !== FirmType.SEF,
      container: EntityGroup,
      render: (value: Entity): React.ReactNode => <EntityWidget entity={value} />,
      required: false,
      fields: [
        {
          name: 'legalName',
          fieldType: FieldType.text,
          label: 'Legal Name',
          display: true,
          required: true,
        },
        {
          name: 'businessAddress',
          fieldType: FieldType.longText,
          label: 'Address',
          display: true,
          required: false,
        },
        {
          name: 'workPhone',
          fieldType: FieldType.phone,
          label: 'Phone Number',
          display: true,
          required: false,
        },
        {
          name: 'isNDF',
          fieldType: FieldType.bool,
          label: 'NDF',
          display: true,
          required: false,
        },
        {
          name: 'isFXO',
          fieldType: FieldType.bool,
          label: 'FXO',
          display: true,
          required: false,
        },
        {
          name: 'orgCode',
          fieldType: FieldType.text,
          label: 'Organization Code',
          display: true,
          required: true,
        },
        {
          name: 'tradingEntityCode',
          fieldType: FieldType.text,
          label: 'Trading Entity Code',
          display: true,
          required: true,
        },
        {
          name: 'legalEntityID',
          fieldType: FieldType.text,
          label: 'Legal Entity ID',
          display: true,
          required: true,
        },
        {
          name: 'MSEntityID',
          fieldType: FieldType.text,
          label: 'MS Entity ID',
          display: true,
          required: false,
        },
        {
          name: 'MSBranchID',
          fieldType: FieldType.text,
          label: 'MS Branch ID',
          display: true,
          required: false,
        },
        {
          name: 'TRTNEntityID',
          fieldType: FieldType.text,
          label: 'TRTN Entity ID',
          display: true,
          required: false,
        },
        {
          name: 'UBSEntityID',
          fieldType: FieldType.text,
          label: 'UBS EntityID',
          display: true,
          required: false,
        },
        {
          name: 'MSCOEntityID',
          fieldType: FieldType.text,
          label: 'MSCO Entity ID',
          display: true,
          required: false,
        },
        {
          name: 'SANTEntityID',
          fieldType: FieldType.text,
          label: 'SANT Entity ID',
          display: true,
          required: false,
        },
        {
          name: 'brokerOfCredit',
          fieldType: FieldType.email,
          label: 'Broker Of Credit',
          display: true,
          required: true,
        },
      ],
    },

    {
      name: 'brokerage',
      fieldType: FieldType.fieldset,
      display: (firm: Firm): boolean => {
        return isNDForFXO(firm) && firm.firmType !== FirmType.STP && firm.firmType !== FirmType.SEF;
      },
      label: 'Brokerage',
      children: [
        {
          name: 'brokerage',
          fieldType: FieldType.custom,
          required: false,
          display: true,
          component: (props: CustomFieldProps<Brokerage>): React.ReactElement => {
            return <BrokerageWidget brokerage={props.value} onChange={props.onChange} />;
          },
          isValid: (_: Brokerage): boolean => true,
        },
      ],
    },
  ];
};
